import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { ImageContainer, LoaderBar, ScreenHeight, TitleText } from 'components'
import { useAuth } from 'context/user'
import { CommanAuthScreenLogin } from 'elements'
import { Images } from 'assets'
import { useTabletAndBelowMediaQuery } from 'layout'
import { countryLabel, countryMatches, exactCountries, normalizeDial, PHONE_ONBOARDING_ENABLED, validatePhone } from 'utility/phone-onboarding'
import { PhoneOnboardingService } from 'utility/services/phone-onboarding'

const Note = styled.p`
    margin: 8px 0 0;
    color: #5c6b80;
    font-size: 13px;
    font-weight: 600;
    line-height: 1.45;
    text-align: center;
`
const Field = styled.label`
    display: block;
    width: 100%;
    margin-top: 14px;
    font-size: 12px;
    font-weight: 800;
    color: #142033;
`
const Row = styled.div`
    display: grid;
    grid-template-columns: 168px minmax(0, 1fr);
    gap: 8px;
    margin-top: 6px;
    @media (max-width: 520px) { grid-template-columns: 1fr; }
`
const CountryWrap = styled.div`position: relative;`
const CodeInput = styled.input`
    width: 100%;
    height: 42px;
    border: 1px solid #d5dbe8;
    border-radius: 8px;
    background: #fff;
    padding: 0 10px;
    font-weight: 800;
    color: #1b2587;
`
const Caption = styled.p`
    margin: 6px 0 0;
    min-height: 18px;
    color: #1b2587;
    font-size: 12.5px;
    font-weight: 800;
`
const List = styled.div`
    position: absolute;
    z-index: 5;
    top: 46px;
    left: 0;
    width: min(320px, 80vw);
    background: #fff;
    border: 1px solid #e4e8f1;
    border-radius: 10px;
    box-shadow: 0 12px 30px rgba(20, 32, 51, 0.12);
    padding: 8px;
`
const Options = styled.div`
    max-height: 220px;
    overflow: auto;
    margin-top: 6px;
    button {
        width: 100%;
        border: 0;
        background: transparent;
        text-align: left;
        padding: 8px;
        border-radius: 8px;
        cursor: pointer;
        font-weight: 700;
    }
    button:hover, button.on { background: #eef1fb; }
    small { display: block; color: #5c6b80; font-weight: 600; }
`
const Phone = styled.div`
    display: flex;
    align-items: center;
    height: 42px;
    border: 1px solid ${p => (p.$bad ? '#b42318' : '#d5dbe8')};
    border-radius: 8px;
    background: #fff;
    overflow: hidden;
    span {
        padding: 0 10px;
        font-weight: 800;
        color: #1b2587;
        border-right: 1px solid #e4e8f1;
        height: 100%;
        display: flex;
        align-items: center;
    }
    input {
        flex: 1;
        min-width: 0;
        height: 100%;
        border: 0;
        padding: 0 10px;
        font-weight: 700;
        font-size: 15px;
    }
    input:focus { outline: none; }
`
const ErrorText = styled.p`
    margin: 8px 0 0;
    color: #b42318;
    font-size: 13px;
    font-weight: 700;
`
const Go = styled.button`
    width: 100%;
    height: 44px;
    margin-top: 16px;
    border: 0;
    border-radius: 8px;
    background: #1b2587;
    color: #fff;
    font-weight: 800;
    font-size: 15px;
    cursor: pointer;
    &:disabled { opacity: 0.6; cursor: default; }
`
const Ok = styled.div`
    width: 100%;
    margin-top: 16px;
    background: #eef1fb;
    color: #1b2587;
    border-radius: 10px;
    padding: 12px;
    font-weight: 700;
    font-size: 13px;
    line-height: 1.45;
`

const missingApi = error => !error?.status || error.status === 404

export const PhoneOnboardingScreen = () => {
    const navigate = useNavigate()
    const { user, updateUser, isAuthenticated } = useAuth()
    const isMobile = useTabletAndBelowMediaQuery()
    const [iso, setIso] = useState('IN')
    const [code, setCode] = useState('+91')
    const [number, setNumber] = useState('')
    const [open, setOpen] = useState(false)
    const [error, setError] = useState('')
    const [saving, setSaving] = useState(false)
    const [saved, setSaved] = useState(null)

    const exact = exactCountries(code)
    const picked = exact.find(item => item.iso === iso) || null
    const [firstExact] = exact
    const country = picked || firstExact || { dial: normalizeDial(code) || '+', name: '' }
    const options = useMemo(() => countryMatches(code), [code])
    const caption = picked ? picked.name : countryLabel(exact)

    useEffect(() => {
        if (!PHONE_ONBOARDING_ENABLED) navigate('/app/task', { replace: true })
    }, [navigate])

    useEffect(() => {
        const close = event => {
            if (!event.target.closest('[data-country]')) setOpen(false)
        }
        document.addEventListener('click', close)
        return () => document.removeEventListener('click', close)
    }, [])

    const enterApp = () => {
        const params = new URLSearchParams(window.location.search)
        const returnUrl = params.get('returnUrl')
        if (returnUrl && returnUrl.startsWith('/app')) {
            navigate(decodeURIComponent(returnUrl))
            return
        }
        navigate('/app/task')
    }

    const submit = async event => {
        event.preventDefault()
        const result = validatePhone(iso, number, code)
        if (!result.ok) {
            setError(result.error)
            setSaved(null)
            return
        }
        setError('')
        setSaving(true)
        let nextUser = {
            ...(user || {}),
            Phone: result.phone,
            PhoneNo: result.phone,
            PhoneE164: result.phone,
            PhoneOnboardingRequired: false,
        }
        let apiMissing = true
        try {
            const data = await Promise.race([
                PhoneOnboardingService.save({
                    countryCode: result.country.dial,
                    nationalNumber: result.national,
                    platform: 'Web',
                }),
                new Promise((_, reject) => {
                    setTimeout(() => reject({ status: 0 }), 6000)
                }),
            ])
            apiMissing = false
            if (data && typeof data === 'object') nextUser = { ...nextUser, ...data, PhoneOnboardingRequired: false }
        } catch (err) {
            if (!missingApi(err)) {
                const body = err?.error
                setError(body?.Message || body?.message || 'The number could not be saved. Check it and try again.')
                setSaving(false)
                return
            }
        }
        if (isAuthenticated && updateUser) updateUser(nextUser)
        setSaved({ phone: result.phone, apiMissing })
        setSaving(false)
    }

    return (
        <ScreenHeight className="container">
            <CommanAuthScreenLogin isMobile={isMobile}>
                {saving && <LoaderBar content="Saving" />}
                <TitleText>
                    <ImageContainer src={Images.Logo} className="img" />
                    <span>Add your phone</span>
                </TitleText>
                <Note>
                    First sign-in needs a phone number with a country code. This is required before the app opens.
                    Google does not fill this in.
                </Note>
                <form onSubmit={submit} style={{ width: '100%' }}>
                    <Field>
                        Phone number
                        <Row>
                            <CountryWrap data-country>
                                <CodeInput
                                    value={code}
                                    aria-label="Country code"
                                    placeholder="+355"
                                    onFocus={() => setOpen(true)}
                                    onChange={event => {
                                        const raw = event.target.value
                                        const hasLetter = /[a-z]/i.test(raw)
                                        const next = hasLetter ? raw : (normalizeDial(raw) || '')
                                        setCode(next)
                                        setOpen(true)
                                        const matches = exactCountries(next)
                                        if (matches.length === 1) setIso(matches[0].iso)
                                        else if (!matches.some(item => item.iso === iso)) setIso('')
                                    }}
                                />
                                {open && (
                                    <List>
                                        <Options>
                                            {options.slice(0, 8).map(item => (
                                                <button
                                                    key={item.iso}
                                                    type="button"
                                                    className={item.iso === iso ? 'on' : ''}
                                                    onClick={() => {
                                                        setIso(item.iso)
                                                        setCode(item.dial)
                                                        setOpen(false)
                                                    }}
                                                >
                                                    {item.name}
                                                    <small>{item.dial}</small>
                                                </button>
                                            ))}
                                            {!options.length && <small>No country uses that code. Check the digits.</small>}
                                        </Options>
                                    </List>
                                )}
                            </CountryWrap>
                            <Phone $bad={Boolean(error)}>
                                <span>{country.dial}</span>
                                <input
                                    value={number}
                                    inputMode="tel"
                                    autoComplete="tel-national"
                                    placeholder="98450 11220"
                                    aria-label="Phone number"
                                    onChange={event => setNumber(event.target.value)}
                                />
                            </Phone>
                        </Row>
                        <Caption>{caption}</Caption>
                    </Field>
                    {error && <ErrorText>{error}</ErrorText>}
                    {saved && (
                        <Ok>
                            Saved as {saved.phone}.
                            {saved.apiMissing
                                ? ' The phone API is not on the server yet, so this sign-in kept the number in the browser. Sales email starts when that API is live.'
                                : ' Your account now has this number.'}
                        </Ok>
                    )}
                    {!saved && (
                        <Go type="submit" disabled={saving}>Continue</Go>
                    )}
                    {saved && (
                        <Go type="button" onClick={enterApp}>Enter the app</Go>
                    )}
                </form>
            </CommanAuthScreenLogin>
        </ScreenHeight>
    )
}
